import React, { useState } from 'react';
import { StagedExternalLinkRecord } from '../../services/api/domains/products/types';
import {
  EXTERNAL_LINK_DISPLAY_FIELDS,
  getStagedRecordKey,
  resolveExternalLinkFieldRawValue,
  resolveExternalLinkFieldValue,
} from '../../domains/products/adapters';
import LgmButton from '../LgmButton';
import TeCodeHeaderTooltip from '../TeCodeHeaderTooltip';
import TeCodeValue from '../TeCodeValue';
import ApplTypeHeaderTooltip from '../ApplTypeHeaderTooltip';
import ApplTypeValue from '../ApplTypeValue';
import ObTypeHeaderTooltip from '../ObTypeHeaderTooltip';
import ObTypeValue from '../ObTypeValue';
import DeaScheduleHeaderTooltip from '../DeaScheduleHeaderTooltip';
import DmfStatusHeaderTooltip from '../DmfStatusHeaderTooltip';
import DmfTypeHeaderTooltip from '../DmfTypeHeaderTooltip';
import { GRAY_200, GRAY_50, GRAY_500, GRAY_900 } from 'theme/grayScale';

// ---------------------------------------------------------------------------
// ExternalLinkReviewList — issue #622, Sub-task 9.4
// ---------------------------------------------------------------------------

export interface ExternalLinkReviewListProps {
  sourceLabel: string;
  /**
   * Generic on purpose: fed by Sub-task 9.2's search checkboxes today,
   * `addedVia: 'search'`; Sub-task 9.3's suggestion checkboxes will push
   * into this SAME array shape, `addedVia: 'suggestion'`, deduped by the
   * identical `(source, record_id)` key — see
   * `getStagedRecordKey`/`StagedExternalLinkRecord` (types.ts).
   */
  records: StagedExternalLinkRecord[];
  onRemove: (record: StagedExternalLinkRecord) => void;
}

/**
 * Staged-record review list inside the "Link to Source" modal (Sub-task
 * 9.4). Purely a staging view — it does NOT call the create-link API or
 * render a commit confirmation; that is Sub-task 9.5's job. The "view
 * details" control expands inline (matching OrangeBookPanel's own
 * "More details" accordion pattern) rather than opening a further nested
 * dialog, so this component doesn't need to reach for the modal-nesting
 * z-index budget Sub-task 9.5 will need for its own confirmation dialog.
 */
const ExternalLinkReviewList: React.FC<ExternalLinkReviewListProps> = ({
  sourceLabel,
  records,
  onRemove,
}) => {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  const toggleExpanded = (key: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (records.length === 0) {
    return (
      <div style={styles.container}>
        <span style={styles.label}>Selected for review</span>
        <div style={styles.emptyState}>
          No records selected yet. Check a search result on the left to add it
          here.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <span style={styles.label}>Selected for review ({records.length})</span>
      <ul
        style={styles.list}
        aria-label={`${sourceLabel} records staged for linking`}>
        {records.map(staged => {
          const key = getStagedRecordKey(staged);
          const expanded = expandedKeys.has(key);
          const fields = EXTERNAL_LINK_DISPLAY_FIELDS[staged.source];
          const summaryFields = fields.filter(f => f.summary);
          const detailFields = fields.filter(f => !f.summary);

          return (
            <li key={key} style={styles.row}>
              <div style={styles.rowHeader}>
                <div style={styles.rowContent}>
                  <span style={styles.rowTitle}>
                    {staged.record.matched_value}
                  </span>
                  <div style={styles.rowSummary}>
                    {summaryFields.map(field => (
                      <span key={field.key} style={styles.summaryField}>
                        <strong>{field.label}:</strong>{' '}
                        {resolveExternalLinkFieldValue(staged.record, field)}
                      </span>
                    ))}
                  </div>
                </div>
                <div style={styles.rowActions}>
                  <LgmButton
                    variant="flat"
                    onClick={() => toggleExpanded(key)}
                    aria-expanded={expanded}
                    style={{ fontSize: '0.75rem', padding: '4px 8px' }}>
                    {expanded ? 'Hide details' : 'View details'}
                  </LgmButton>
                  <LgmButton
                    variant="flat"
                    color="danger"
                    onClick={() => onRemove(staged)}
                    aria-label={`Remove ${staged.record.matched_value} from review list`}
                    style={{ fontSize: '0.75rem', padding: '4px 8px' }}>
                    Remove
                  </LgmButton>
                </div>
              </div>

              {expanded && detailFields.length > 0 && (
                <div style={styles.details}>
                  {detailFields.map(field => (
                    <div key={field.key} style={styles.detailRow}>
                      <span style={styles.detailLabel}>
                        {field.label}
                        {/* This modal nests above the base 10000 overlay at
                            content zIndex 10002 (LinkToSourceModal.tsx) --
                            InfoTooltip's own 10001 default clears the base
                            modal but not this layer. Matches
                            ExternalLinkSuggestionsPane.tsx's own override
                            for the same modal. */}
                        {field.key === 'te_code' && (
                          <TeCodeHeaderTooltip zIndex={10003} />
                        )}
                        {field.key === 'appl_type' && (
                          <ApplTypeHeaderTooltip zIndex={10003} />
                        )}
                        {field.key === 'type' && (
                          <ObTypeHeaderTooltip zIndex={10003} />
                        )}
                        {field.key === 'dea_schedule' && (
                          <DeaScheduleHeaderTooltip zIndex={10003} />
                        )}
                        {field.key === 'status' && (
                          <DmfStatusHeaderTooltip zIndex={10003} />
                        )}
                        {field.key === 'dmf_type' && (
                          <DmfTypeHeaderTooltip zIndex={10003} />
                        )}
                      </span>
                      <span style={styles.detailValue}>
                        {field.key === 'te_code' ? (
                          <TeCodeValue
                            code={resolveExternalLinkFieldRawValue(
                              staged.record,
                              field
                            )}
                            zIndex={10003}
                          />
                        ) : field.key === 'appl_type' ? (
                          <ApplTypeValue
                            code={resolveExternalLinkFieldRawValue(
                              staged.record,
                              field
                            )}
                            zIndex={10003}
                          />
                        ) : field.key === 'type' ? (
                          <ObTypeValue
                            code={resolveExternalLinkFieldRawValue(
                              staged.record,
                              field
                            )}
                            zIndex={10003}
                          />
                        ) : (
                          resolveExternalLinkFieldValue(staged.record, field)
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    // 2:1 split with LinkToSourceModal's `linkingPanes` (flex: '2 1 0%') --
    // matching flex-basis 0% on both means the split is driven purely by
    // flex-grow, not by search-pane's `minWidth`/basis dominating.
    flex: '1 1 0%',
    minWidth: 0,
  },
  label: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: GRAY_500,
  },
  emptyState: {
    padding: '1rem',
    fontSize: '0.8125rem',
    color: GRAY_500,
    textAlign: 'center',
    border: `1px dashed ${GRAY_200}`,
    borderRadius: '4px',
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.375rem',
    // Fill whatever height `container` (flex column, stretched to match the
    // linkingPanes column's height -- LinkToSourceModal's `body` is a flex
    // row) ends up with, instead of self-capping at a fixed 24rem
    // regardless of how much modal height is actually available.
    flex: '1 1 0%',
    minHeight: 0,
    overflowY: 'auto',
  },
  row: {
    border: `1px solid ${GRAY_200}`,
    borderRadius: '4px',
    backgroundColor: GRAY_50,
    padding: '0.5rem',
  },
  rowHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '0.5rem',
  },
  rowContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.125rem',
    minWidth: 0,
  },
  rowTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: GRAY_900,
  },
  rowSummary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
    fontSize: '0.75rem',
    color: GRAY_500,
  },
  summaryField: {
    whiteSpace: 'nowrap',
  },
  rowActions: {
    display: 'flex',
    gap: '0.25rem',
    flexShrink: 0,
  },
  details: {
    marginTop: '0.5rem',
    paddingTop: '0.5rem',
    borderTop: `1px solid ${GRAY_200}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  detailRow: {
    display: 'flex',
    gap: '0.5rem',
    fontSize: '0.8125rem',
  },
  detailLabel: {
    minWidth: '9rem',
    fontWeight: 600,
    color: GRAY_500,
    flexShrink: 0,
  },
  detailValue: {
    color: GRAY_900,
    wordBreak: 'break-word',
  },
};

export default ExternalLinkReviewList;
